import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../store';
import { useAlertRules } from '../api/alerts';
import { useGPU } from '../hooks/useGPU';
import { ModelCache } from '../gpu/model-cache';
import { benchmarkAll } from '../gpu/benchmark';
import type { FullBenchmarkReport } from '../gpu/benchmark';
import apiClient from '../api/client';

// ── Types ──

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used: string | null;
  status: 'active' | 'revoked';
}

interface DataSource {
  id: string;
  name: string;
  type: string;
  status: 'connected' | 'disconnected' | 'error';
  last_sync: string | null;
  records_count: number;
}

interface SystemHealth {
  service: string;
  status: 'healthy' | 'degraded' | 'down';
  latency_ms: number;
  uptime_percent: number;
  last_check: string;
}

interface OsintKeyDef {
  id: string;
  label: string;
  description: string;
  category: 'threat_intel' | 'email_identity' | 'network_dns' | 'search';
  secondaryId?: string;
  secondaryLabel?: string;
}

type OsintKeysState = Record<string, string>;

type SettingsTab = 'api_keys' | 'osint_keys' | 'alerts' | 'data_sources' | 'preferences' | 'gpu' | 'system';

const TABS: { value: SettingsTab; label: string; icon: string; desc: string }[] = [
  { value: 'api_keys', label: 'Clés API', desc: 'Accès programmatique', icon: 'M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z' },
  { value: 'osint_keys', label: 'Clés OSINT', desc: 'Sources externes', icon: 'M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418' },
  { value: 'alerts', label: 'Règles d\'alerte', desc: 'Déclencheurs automatiques', icon: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0' },
  { value: 'data_sources', label: 'Sources de données', desc: 'Connexions et flux', icon: 'M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125' },
  { value: 'preferences', label: 'Préférences', desc: 'Apparence et langue', icon: 'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z' },
  { value: 'gpu', label: 'GPU Compute', desc: 'Accélération matérielle', icon: 'M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25z' },
  { value: 'system', label: 'Santé système', desc: 'Services et performances', icon: 'M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9' },
];

// ── OSINT Key definitions ──

const OSINT_KEY_DEFS: OsintKeyDef[] = [
  { id: 'shodan', label: 'Shodan', description: 'Moteur de recherche d\'appareils connectés. Scan IP, ports et services.', category: 'threat_intel' },
  { id: 'virustotal', label: 'VirusTotal', description: 'Scanner multi-moteur de malwares. Analyse fichiers, URLs, domaines et IPs.', category: 'threat_intel' },
  { id: 'alienvault_otx', label: 'AlienVault OTX', description: 'Open Threat Exchange. Renseignements communautaires sur les menaces et IoCs.', category: 'threat_intel' },
  { id: 'greynoise', label: 'GreyNoise', description: 'Analyseur de bruit Internet. Distingue les scans de masse des attaques ciblées.', category: 'threat_intel' },
  { id: 'hibp', label: 'Have I Been Pwned', description: 'Vérifie si des emails ou mots de passe apparaissent dans des fuites de données.', category: 'email_identity' },
  { id: 'hunter_io', label: 'Hunter.io', description: 'Recherche et vérification d\'emails. Découvre les adresses associées à un domaine.', category: 'email_identity' },
  { id: 'abuseipdb', label: 'AbuseIPDB', description: 'Base de signalement d\'abus IP. Vérification et rapport d\'adresses malveillantes.', category: 'network_dns' },
  { id: 'securitytrails', label: 'SecurityTrails', description: 'Intelligence DNS et domaines. Historique DNS, sous-domaines et WHOIS.', category: 'network_dns' },
  { id: 'censys_id', label: 'Censys', description: 'Données de scan Internet. Recherche certificats, hôtes et sites web. Requiert API ID et Secret.', category: 'network_dns', secondaryId: 'censys_secret', secondaryLabel: 'Censys API Secret' },
  { id: 'brave_search', label: 'Brave Search', description: 'API de recherche web respectueuse de la vie privée.', category: 'search' },
];

const CATEGORY_LABELS: Record<string, string> = {
  threat_intel: 'Renseignement sur les menaces',
  email_identity: 'Email et identité',
  network_dns: 'Réseau et DNS',
  search: 'Recherche',
};

const CATEGORY_ORDER = ['threat_intel', 'email_identity', 'network_dns', 'search'] as const;

const STORAGE_KEY = 'helios_osint_keys';

// Mock data
const MOCK_API_KEYS: ApiKey[] = [
  { id: '1', name: 'Production API', prefix: 'hel_prod_****', created_at: '2026-01-15T10:00:00Z', last_used: '2026-03-16T08:30:00Z', status: 'active' },
  { id: '2', name: 'Development', prefix: 'hel_dev_****', created_at: '2026-02-20T14:00:00Z', last_used: '2026-03-15T22:00:00Z', status: 'active' },
  { id: '3', name: 'Deprecated Key', prefix: 'hel_old_****', created_at: '2025-06-01T10:00:00Z', last_used: null, status: 'revoked' },
];

const MOCK_DATA_SOURCES: DataSource[] = [
  { id: '1', name: 'Neo4j Graph DB', type: 'base de données', status: 'connected', last_sync: '2026-03-16T08:00:00Z', records_count: 124560 },
  { id: '2', name: 'OSINT Feed - Shodan', type: 'api', status: 'connected', last_sync: '2026-03-16T07:45:00Z', records_count: 89230 },
  { id: '3', name: 'Threat Intel Feed', type: 'flux', status: 'connected', last_sync: '2026-03-16T06:00:00Z', records_count: 45670 },
  { id: '4', name: 'Blockchain Monitor', type: 'api', status: 'disconnected', last_sync: '2026-03-14T12:00:00Z', records_count: 12340 },
  { id: '5', name: 'Dark Web Crawler', type: 'crawler', status: 'error', last_sync: null, records_count: 0 },
];

const MOCK_SYSTEM_HEALTH: SystemHealth[] = [
  { service: 'Xawio API', status: 'healthy', latency_ms: 45, uptime_percent: 99.98, last_check: '2026-03-16T09:00:00Z' },
  { service: 'Neo4j Database', status: 'healthy', latency_ms: 12, uptime_percent: 99.99, last_check: '2026-03-16T09:00:00Z' },
  { service: 'Redis Cache', status: 'healthy', latency_ms: 2, uptime_percent: 100, last_check: '2026-03-16T09:00:00Z' },
  { service: 'Cortex ML Engine', status: 'degraded', latency_ms: 890, uptime_percent: 98.5, last_check: '2026-03-16T09:00:00Z' },
  { service: 'OSINT Collector', status: 'healthy', latency_ms: 230, uptime_percent: 99.7, last_check: '2026-03-16T09:00:00Z' },
  { service: 'WebSocket Server', status: 'healthy', latency_ms: 8, uptime_percent: 99.95, last_check: '2026-03-16T09:00:00Z' },
];

const STATUS_LABELS: Record<string, string> = {
  connected: 'Connecté',
  disconnected: 'Déconnecté',
  error: 'Erreur',
  healthy: 'Opérationnel',
  degraded: 'Dégradé',
  down: 'Hors service',
};

const STATUS_COLORS_SOURCE: Record<string, string> = {
  connected: 'text-green-400 bg-green-500/20',
  disconnected: 'text-yellow-400 bg-yellow-500/20',
  error: 'text-red-400 bg-red-500/20',
};

const STATUS_COLORS_HEALTH: Record<string, string> = {
  healthy: 'text-green-400 bg-green-500/20',
  degraded: 'text-yellow-400 bg-yellow-500/20',
  down: 'text-red-400 bg-red-500/20',
};

const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critique',
  high: 'Élevée',
  medium: 'Moyenne',
  low: 'Basse',
};

// ── OSINT Key Input Component ──

function OsintKeyInput({
  keyId,
  label,
  description,
  value,
  onChange,
  onTest,
  testStatus,
}: {
  keyId: string;
  label: string;
  description: string;
  value: string;
  onChange: (id: string, value: string) => void;
  onTest: (id: string) => void;
  testStatus: 'idle' | 'testing' | 'success' | 'error';
}) {
  const [visible, setVisible] = useState(false);
  const configured = value.trim().length > 0;

  return (
    <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle space-y-2">
      <div className="flex items-center gap-2">
        {configured && (
          <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
        <span className="text-sm font-medium text-gray-200">{label}</span>
      </div>
      <p className="text-xs text-gray-500">{description}</p>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            type={visible ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange(keyId, e.target.value)}
            placeholder={`Entrez la clé ${label}...`}
            className="w-full bg-surface-secondary border border-border-subtle rounded-lg px-3 py-2 pr-10 text-sm text-gray-200 placeholder-gray-500 font-mono focus:outline-none focus:border-cyber-cyan/50"
          />
          <button
            type="button"
            onClick={() => setVisible(!visible)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
            title={visible ? 'Masquer' : 'Afficher'}
          >
            {visible ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>
        <button
          onClick={() => onTest(keyId)}
          disabled={!configured || testStatus === 'testing'}
          className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border flex items-center gap-1.5 whitespace-nowrap disabled:opacity-40 ${
            testStatus === 'success'
              ? 'bg-green-500/10 text-green-400 border-green-500/20'
              : testStatus === 'error'
              ? 'bg-red-500/10 text-red-400 border-red-500/20'
              : 'bg-surface-secondary text-gray-400 border-border-subtle hover:text-gray-200 hover:border-cyber-cyan/30'
          }`}
        >
          {testStatus === 'testing' ? (
            <>
              <div className="w-3 h-3 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" />
              Test...
            </>
          ) : testStatus === 'success' ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              Valide
            </>
          ) : testStatus === 'error' ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Échoué
            </>
          ) : (
            'Tester'
          )}
        </button>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('api_keys');
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateKey, setShowCreateKey] = useState(false);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const { data: alertRules } = useAlertRules();

  // GPU state
  const { isReady: gpuReady, backend: gpuBackend, capabilities: gpuCapabilities } = useGPU();
  const [gpuEnabled, setGpuEnabled] = useState(true);
  const [cachedModels, setCachedModels] = useState<Array<{ id: string; size: number; cached_at: string }>>([]);
  const [cacheSize, setCacheSize] = useState(0);
  const [benchmarkReport, setBenchmarkReport] = useState<FullBenchmarkReport | null>(null);
  const [benchmarkRunning, setBenchmarkRunning] = useState(false);

  // OSINT keys state
  const [osintKeys, setOsintKeys] = useState<OsintKeysState>({});
  const [osintTestStatus, setOsintTestStatus] = useState<Record<string, 'idle' | 'testing' | 'success' | 'error'>>({});
  const [osintSaving, setOsintSaving] = useState(false);
  const [osintSaveMsg, setOsintSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const modelCache = new ModelCache();

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        setOsintKeys(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
  }, []);

  const handleOsintKeyChange = useCallback((id: string, value: string) => {
    setOsintKeys((prev) => ({ ...prev, [id]: value }));
    setOsintTestStatus((prev) => ({ ...prev, [id]: 'idle' }));
    setOsintSaveMsg(null);
  }, []);

  const handleTestOsintKey = useCallback(async (keyId: string) => {
    setOsintTestStatus((prev) => ({ ...prev, [keyId]: 'testing' }));
    try {
      await apiClient.post('/settings/osint-keys/test', { key_id: keyId, value: osintKeys[keyId] || '' });
      setOsintTestStatus((prev) => ({ ...prev, [keyId]: 'success' }));
    } catch {
      setOsintTestStatus((prev) => ({ ...prev, [keyId]: 'error' }));
    }
  }, [osintKeys]);

  const handleSaveOsintKeys = useCallback(async () => {
    setOsintSaving(true);
    setOsintSaveMsg(null);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(osintKeys));
      await apiClient.post('/settings/osint-keys', osintKeys);
      setOsintSaveMsg({ type: 'success', text: 'Toutes les clés OSINT ont été enregistrées.' });
    } catch {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(osintKeys));
      setOsintSaveMsg({ type: 'error', text: 'Enregistré localement. La synchronisation backend a échoué.' });
    } finally {
      setOsintSaving(false);
    }
  }, [osintKeys]);

  const configuredCount = OSINT_KEY_DEFS.reduce((count, def) => {
    const hasMain = (osintKeys[def.id] || '').trim().length > 0;
    const hasSecondary = def.secondaryId ? (osintKeys[def.secondaryId] || '').trim().length > 0 : true;
    return count + (hasMain && hasSecondary ? 1 : 0);
  }, 0);

  const refreshModelCache = useCallback(async () => {
    const models = await modelCache.listModels();
    setCachedModels(models);
    const size = await modelCache.getCacheSize();
    setCacheSize(size);
  }, []);

  useEffect(() => {
    if (activeTab === 'gpu') {
      refreshModelCache();
    }
  }, [activeTab, refreshModelCache]);

  const handleClearCache = useCallback(async () => {
    await modelCache.clearCache();
    await refreshModelCache();
  }, [refreshModelCache]);

  const handleRunBenchmark = useCallback(async () => {
    setBenchmarkRunning(true);
    try {
      const report = await benchmarkAll();
      setBenchmarkReport(report);
    } catch (err) {
      console.error('[Settings] Benchmark failed:', err);
    } finally {
      setBenchmarkRunning(false);
    }
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Paramètres</h1>
        <p className="text-sm text-gray-400 mt-1">Gérez vos clés API, règles d'alerte, sources de données et préférences</p>
      </div>

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-56 flex-shrink-0">
          <nav className="space-y-0.5 sticky top-6">
            {TABS.map((tab) => (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                  activeTab === tab.value
                    ? 'bg-cyber-cyan/10 text-cyber-cyan border border-cyber-cyan/20'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-surface-tertiary border border-transparent'
                }`}
              >
                <svg className="w-[18px] h-[18px] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={tab.icon} />
                </svg>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{tab.label}</div>
                  <div className={`text-[10px] truncate ${activeTab === tab.value ? 'text-cyber-cyan/60' : 'text-gray-600'}`}>{tab.desc}</div>
                </div>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* ─── Clés API ─── */}
          {activeTab === 'api_keys' && (
            <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-100">Clés API</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Gérez les clés d'accès programmatique à l'API Xawio</p>
                </div>
                <button
                  onClick={() => setShowCreateKey(!showCreateKey)}
                  className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Créer une clé
                </button>
              </div>

              {showCreateKey && (
                <div className="p-4 bg-surface-tertiary rounded-lg border border-cyber-cyan/20 space-y-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Nom de la clé</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="ex. Clé API Production"
                      className="w-full bg-surface-secondary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyber-cyan/50"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowCreateKey(false); setNewKeyName(''); }}
                      className="px-3 py-1.5 rounded-lg bg-surface-secondary border border-border-subtle text-gray-400 hover:text-gray-200 text-sm transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      disabled={!newKeyName.trim()}
                      className="px-3 py-1.5 rounded-lg bg-cyber-cyan text-gray-900 text-sm font-medium disabled:opacity-50 hover:opacity-90 transition-opacity"
                    >
                      Générer
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {MOCK_API_KEYS.map((key) => (
                  <div key={key.id} className="flex items-center justify-between p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${key.status === 'active' ? 'bg-green-400' : 'bg-gray-500'}`} />
                      <div>
                        <div className="text-sm font-medium text-gray-200">{key.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{key.prefix}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Créée le {new Date(key.created_at).toLocaleDateString('fr-FR')}</div>
                        <div className="text-xs text-gray-500">
                          {key.last_used ? `Dernière utilisation le ${new Date(key.last_used).toLocaleDateString('fr-FR')}` : 'Jamais utilisée'}
                        </div>
                      </div>
                      {key.status === 'active' && (
                        <button className="px-2.5 py-1 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors border border-red-500/20">
                          Révoquer
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Clés OSINT ─── */}
          {activeTab === 'osint_keys' && (
            <div className="space-y-5">
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-100">Clés API OSINT</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Configurez les clés pour les sources de données externes.{' '}
                      <span className="text-cyber-cyan">{configuredCount}/{OSINT_KEY_DEFS.length} configurées</span>
                    </p>
                  </div>
                  <button
                    onClick={handleSaveOsintKeys}
                    disabled={osintSaving}
                    className="px-4 py-2 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                  >
                    {osintSaving ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        Tout enregistrer
                      </>
                    )}
                  </button>
                </div>

                {osintSaveMsg && (
                  <div className={`mt-3 px-3 py-2 rounded-lg text-xs border ${
                    osintSaveMsg.type === 'success'
                      ? 'bg-green-500/10 text-green-400 border-green-500/20'
                      : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20'
                  }`}>
                    {osintSaveMsg.text}
                  </div>
                )}
              </div>

              {CATEGORY_ORDER.map((category) => {
                const keys = OSINT_KEY_DEFS.filter((k) => k.category === category);
                if (keys.length === 0) return null;
                return (
                  <div key={category} className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-4">
                    <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                      {CATEGORY_LABELS[category]}
                    </h3>
                    <div className="space-y-3">
                      {keys.map((def) => (
                        <div key={def.id}>
                          <OsintKeyInput
                            keyId={def.id}
                            label={def.label}
                            description={def.description}
                            value={osintKeys[def.id] || ''}
                            onChange={handleOsintKeyChange}
                            onTest={handleTestOsintKey}
                            testStatus={osintTestStatus[def.id] || 'idle'}
                          />
                          {def.secondaryId && (
                            <div className="mt-2 ml-4">
                              <OsintKeyInput
                                keyId={def.secondaryId}
                                label={def.secondaryLabel || def.secondaryId}
                                description="Secret complémentaire requis pour l'API Censys ci-dessus."
                                value={osintKeys[def.secondaryId] || ''}
                                onChange={handleOsintKeyChange}
                                onTest={handleTestOsintKey}
                                testStatus={osintTestStatus[def.secondaryId] || 'idle'}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ─── Règles d'alerte ─── */}
          {activeTab === 'alerts' && (
            <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-100">Règles d'alerte</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Configurez les déclencheurs automatiques d'alertes</p>
                </div>
                <button className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Ajouter une règle
                </button>
              </div>

              <div className="space-y-2">
                {alertRules?.map((rule) => (
                  <div key={rule.id} className="flex items-center justify-between p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${rule.enabled ? 'bg-green-400' : 'bg-gray-500'}`} />
                      <div>
                        <div className="text-sm font-medium text-gray-200">{rule.name}</div>
                        <div className="text-xs text-gray-500">{rule.description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${
                        rule.severity === 'critical' ? 'text-red-400 bg-red-500/20 border-red-500/30' :
                        rule.severity === 'high' ? 'text-orange-400 bg-orange-500/20 border-orange-500/30' :
                        rule.severity === 'medium' ? 'text-yellow-400 bg-yellow-500/20 border-yellow-500/30' :
                        'text-blue-400 bg-blue-500/20 border-blue-500/30'
                      }`}>
                        {SEVERITY_LABELS[rule.severity] || rule.severity}
                      </span>
                      <button
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          rule.enabled ? 'bg-cyber-cyan' : 'bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          rule.enabled ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  </div>
                )) || (
                  <p className="text-sm text-gray-500 py-4 text-center">Chargement des règles d'alerte...</p>
                )}
              </div>
            </div>
          )}

          {/* ─── Sources de données ─── */}
          {activeTab === 'data_sources' && (
            <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Sources de données</h2>
                <p className="text-xs text-gray-500 mt-0.5">Gérez les sources de données et flux connectés</p>
              </div>

              <div className="space-y-2">
                {MOCK_DATA_SOURCES.map((source) => (
                  <div key={source.id} className="flex items-center justify-between p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS_SOURCE[source.status] || ''}`}>
                        {STATUS_LABELS[source.status] || source.status}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-200">{source.name}</div>
                        <div className="text-xs text-gray-500 capitalize">{source.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">{source.records_count.toLocaleString('fr-FR')} enregistrements</div>
                        <div className="text-xs text-gray-500">
                          {source.last_sync ? `Synchronisé le ${new Date(source.last_sync).toLocaleString('fr-FR')}` : 'Jamais synchronisé'}
                        </div>
                      </div>
                      <button className="p-1.5 rounded-lg text-gray-400 hover:text-cyber-cyan hover:bg-surface-secondary transition-colors" title="Synchroniser">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ─── Préférences ─── */}
          {activeTab === 'preferences' && (
            <div className="space-y-5">
              {/* Apparence */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <h2 className="text-lg font-semibold text-gray-100 mb-4">Apparence</h2>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-200">Thème</div>
                    <div className="text-xs text-gray-500">Basculer entre le mode sombre et clair</div>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-tertiary border border-border-subtle text-sm text-gray-300 hover:text-gray-100 transition-colors"
                  >
                    {theme === 'dark' ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                      </svg>
                    )}
                    {theme === 'dark' ? 'Sombre' : 'Clair'}
                  </button>
                </div>
              </div>

              {/* Notifications */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <h2 className="text-lg font-semibold text-gray-100 mb-4">Notifications</h2>
                <div className="space-y-4">
                  {[
                    { label: 'Alertes critiques', description: 'Notification immédiate pour les alertes de sévérité critique', default: true },
                    { label: 'Nouvelle entité détectée', description: 'Lorsqu\'une nouvelle entité à haut risque est découverte', default: true },
                    { label: 'Fin de pipeline', description: 'Lorsqu\'un pipeline de données termine son exécution', default: false },
                    { label: 'Rapport prêt', description: 'Lorsqu\'un rapport généré est prêt au téléchargement', default: true },
                    { label: 'Résultats OSINT', description: 'Lorsqu\'une investigation OSINT est terminée', default: true },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-gray-200">{item.label}</div>
                        <div className="text-xs text-gray-500">{item.description}</div>
                      </div>
                      <button
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          item.default ? 'bg-cyber-cyan' : 'bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          item.default ? 'translate-x-4' : 'translate-x-0.5'
                        }`} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Langue et région */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <h2 className="text-lg font-semibold text-gray-100 mb-4">Langue et région</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Langue</label>
                    <select className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50">
                      <option value="fr">Français</option>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="de">Deutsch</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Fuseau horaire</label>
                    <select className="w-full bg-surface-tertiary border border-border-subtle rounded-lg px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyber-cyan/50">
                      <option value="Europe/Paris">Europe/Paris (CET)</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New York (EST)</option>
                      <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ─── GPU Compute ─── */}
          {activeTab === 'gpu' && (
            <div className="space-y-5">
              {/* Capacités GPU */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <h2 className="text-lg font-semibold text-gray-100 mb-4">Capacités GPU</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="text-xs text-gray-500 mb-1">Backend</div>
                    <div className={`text-lg font-bold ${
                      gpuBackend === 'webgpu' ? 'text-green-400' :
                      gpuBackend === 'webgl' ? 'text-yellow-400' : 'text-gray-400'
                    }`}>
                      {gpuReady ? gpuBackend?.toUpperCase() || 'N/A' : 'Initialisation...'}
                    </div>
                  </div>
                  <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="text-xs text-gray-500 mb-1">Statut</div>
                    <div className={`text-lg font-bold ${gpuReady ? 'text-green-400' : 'text-yellow-400'}`}>
                      {gpuReady ? 'Actif' : 'Chargement...'}
                    </div>
                  </div>
                  {gpuCapabilities != null && typeof gpuCapabilities === 'object' ? (
                    <>
                      {(gpuCapabilities as Record<string, unknown>).maxBufferSize && (
                        <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                          <div className="text-xs text-gray-500 mb-1">Taille max. buffer</div>
                          <div className="text-sm font-mono text-gray-200">
                            {(Number((gpuCapabilities as Record<string, unknown>).maxBufferSize) / 1024 / 1024).toFixed(0)} Mo
                          </div>
                        </div>
                      )}
                      {(gpuCapabilities as Record<string, unknown>).maxComputeWorkgroupsPerDimension && (
                        <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                          <div className="text-xs text-gray-500 mb-1">Max Workgroups/Dim</div>
                          <div className="text-sm font-mono text-gray-200">
                            {Number((gpuCapabilities as Record<string, unknown>).maxComputeWorkgroupsPerDimension).toLocaleString('fr-FR')}
                          </div>
                        </div>
                      )}
                      {(gpuCapabilities as Record<string, unknown>).adapterInfo && (
                        <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle col-span-2">
                          <div className="text-xs text-gray-500 mb-1">Adaptateur GPU</div>
                          <div className="text-sm font-mono text-gray-200">
                            {String((gpuCapabilities as Record<string, unknown>).adapterInfo)}
                          </div>
                        </div>
                      )}
                      {(gpuCapabilities as Record<string, unknown>).renderer && (
                        <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle col-span-2">
                          <div className="text-xs text-gray-500 mb-1">Moteur de rendu</div>
                          <div className="text-sm font-mono text-gray-200">
                            {String((gpuCapabilities as Record<string, unknown>).renderer)}
                          </div>
                        </div>
                      )}
                      {(gpuCapabilities as Record<string, unknown>).hardwareConcurrency && (
                        <div className="p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                          <div className="text-xs text-gray-500 mb-1">Coeurs CPU</div>
                          <div className="text-sm font-mono text-gray-200">
                            {String((gpuCapabilities as Record<string, unknown>).hardwareConcurrency)}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </div>

              {/* Accélération GPU */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-200">Accélération GPU</div>
                    <div className="text-xs text-gray-500">Utiliser le GPU pour le layout du graphe, la similarité et l'inférence IA</div>
                  </div>
                  <button
                    onClick={() => setGpuEnabled(!gpuEnabled)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      gpuEnabled ? 'bg-cyber-cyan' : 'bg-gray-600'
                    }`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                      gpuEnabled ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              </div>

              {/* Modèles IA en cache */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-100">Modèles IA en cache</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Taille totale du cache : {(cacheSize / 1024 / 1024).toFixed(1)} Mo
                    </p>
                  </div>
                  <button
                    onClick={handleClearCache}
                    disabled={cachedModels.length === 0}
                    className="px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-xs hover:bg-red-500/20 transition-colors border border-red-500/20 disabled:opacity-40"
                  >
                    Vider le cache
                  </button>
                </div>

                {cachedModels.length > 0 ? (
                  <div className="space-y-2">
                    {cachedModels.map((model) => (
                      <div key={model.id} className="flex items-center justify-between p-3 bg-surface-tertiary rounded-lg border border-border-subtle">
                        <div>
                          <div className="text-sm font-medium text-gray-200">{model.id}</div>
                          <div className="text-xs text-gray-500">
                            En cache depuis le {new Date(model.cached_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        <div className="text-xs font-mono text-gray-400">
                          {(model.size / 1024 / 1024).toFixed(1)} Mo
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4 text-center">
                    Aucun modèle en cache. Les modèles sont téléchargés lors de la première utilisation.
                  </p>
                )}
              </div>

              {/* Benchmark */}
              <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-100">Benchmark de performance</h2>
                    <p className="text-xs text-gray-500 mt-0.5">Testez les performances de calcul GPU</p>
                  </div>
                  <button
                    onClick={handleRunBenchmark}
                    disabled={benchmarkRunning || !gpuReady}
                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-cyber-cyan to-helios-600 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {benchmarkRunning ? (
                      <>
                        <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        En cours...
                      </>
                    ) : (
                      'Lancer le benchmark'
                    )}
                  </button>
                </div>

                {benchmarkReport && (
                  <div className="space-y-2">
                    <div className="text-xs text-gray-500">
                      Backend : {benchmarkReport.backend.toUpperCase()} | Total : {benchmarkReport.totalTime_ms}ms
                    </div>
                    {benchmarkReport.results.map((r, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-surface-tertiary rounded-lg border border-border-subtle">
                        <div className="text-sm text-gray-200">{r.name}</div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs font-mono text-gray-400">{r.throughput}</span>
                          <span className={`text-xs font-mono font-bold ${
                            r.time_ms < 100 ? 'text-green-400' :
                            r.time_ms < 1000 ? 'text-yellow-400' : 'text-red-400'
                          }`}>
                            {r.time_ms}ms
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Santé système ─── */}
          {activeTab === 'system' && (
            <div className="bg-surface-secondary rounded-xl border border-border-subtle p-6 space-y-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-100">Santé du système</h2>
                <p className="text-xs text-gray-500 mt-0.5">Surveillez le statut et les performances des services</p>
              </div>

              {/* Résumé en haut */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-surface-tertiary rounded-lg p-4 border border-border-subtle text-center">
                  <div className="text-2xl font-bold text-green-400">
                    {MOCK_SYSTEM_HEALTH.filter((s) => s.status === 'healthy').length}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Opérationnel</div>
                </div>
                <div className="bg-surface-tertiary rounded-lg p-4 border border-border-subtle text-center">
                  <div className="text-2xl font-bold text-yellow-400">
                    {MOCK_SYSTEM_HEALTH.filter((s) => s.status === 'degraded').length}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Dégradé</div>
                </div>
                <div className="bg-surface-tertiary rounded-lg p-4 border border-border-subtle text-center">
                  <div className="text-2xl font-bold text-red-400">
                    {MOCK_SYSTEM_HEALTH.filter((s) => s.status === 'down').length}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">Hors service</div>
                </div>
              </div>

              <div className="space-y-2">
                {MOCK_SYSTEM_HEALTH.map((service) => (
                  <div key={service.service} className="flex items-center justify-between p-4 bg-surface-tertiary rounded-lg border border-border-subtle">
                    <div className="flex items-center gap-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS_HEALTH[service.status] || ''}`}>
                        {STATUS_LABELS[service.status] || service.status}
                      </span>
                      <span className="text-sm font-medium text-gray-200">{service.service}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Latence</div>
                        <div className={`text-sm font-mono ${
                          service.latency_ms < 100 ? 'text-green-400' :
                          service.latency_ms < 500 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {service.latency_ms}ms
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Disponibilité</div>
                        <div className={`text-sm font-mono ${
                          service.uptime_percent >= 99.9 ? 'text-green-400' :
                          service.uptime_percent >= 99 ? 'text-yellow-400' : 'text-red-400'
                        }`}>
                          {service.uptime_percent}%
                        </div>
                      </div>
                      <div className="text-right w-28">
                        <div className="text-xs text-gray-500">
                          {new Date(service.last_check).toLocaleTimeString('fr-FR')}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
